import React, { useEffect, useState } from "react";
import { TextFractionSpan } from "@fidget/common/components/indicators/textFractionSpan/textFractionSpan";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { InputWithDropdown } from "../../inputWithDropdown/inputWithDropdown";
import { FilterCard } from "../filterCard/filterCard";
import { useSettingsListFilters } from "../useListFilter/useListFilter";
import styles from "./listFilterWrapper.css";
import { useFilterWrapper } from "./useFilterWrapper";

interface Props<T> {
  placeholder: string;
  filteredCount: number;
  allCount: number;
  inputPredicate(item: T, value: string): boolean;
  inputPredicateId: string;
  children?: React.ReactNode;
  headerText: string;
  showCounter?: boolean;
}

export const ListFilterWrapper = <T extends unknown>({
  inputPredicateId,
  children,
  placeholder,
  filteredCount,
  allCount,
  inputPredicate,
  headerText,
  showCounter = true
}: Props<T>) => {
  const { clearActivated, activateFilter, deactivateFilter, activeFilters, activateFilters } =
    useSettingsListFilters<T>();
  const { selectedFilters, deselectAll } = useFilterWrapper<T>();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      deselectAll();
    }
  }, [isOpen]);

  const handleClear = () => {
    clearActivated();
    deselectAll();
    setIsOpen(false);
  };

  const handleSearchClick = () => {
    activateFilters(selectedFilters);
    deselectAll();
    setIsOpen(false);
  };

  const [searchText, setSearchText] = useState("");

  const handleSearchTextChange = (value: string) => {
    setSearchText(value);
    if (value) {
      activateFilter({
        filterId: inputPredicateId,
        predicate: (item) => inputPredicate(item, value)
      });
    } else {
      deactivateFilter(inputPredicateId);
    }
  };

  const renderHeader = () => (
    <>
      <div className={styles.header}>
        <div>{headerText}</div>
        <div className={styles["header-actions"]}>
          <p className={styles["clear-filters"]} onClick={handleClear}>
            {intl.formatMessage("inbox.moreFilters.clear")}
          </p>
          <p className={styles.search} onClick={handleSearchClick}>
            {intl.formatMessage("search")}
          </p>
        </div>
      </div>
      <div className={styles.line} />
    </>
  );

  return (
    <div className={styles.container}>
      <div className={styles["input-container"]}>
        <InputWithDropdown
          placeholder={placeholder}
          value={searchText}
          onChange={(e) => handleSearchTextChange((e.target as HTMLInputElement).value)}
          onArrowClick={() => setIsOpen(!isOpen)}
          onOutsideClick={() => setIsOpen(false)}
          onResetInput={() => handleSearchTextChange("")}
          isOpen={isOpen}
        >
          <div className={styles.dropdown}>
            {renderHeader()}
            {children}
          </div>
        </InputWithDropdown>
      </div>
      <div className={styles["filters-container"]}>
        {Object.values(activeFilters)
          .filter((f) => f.description)
          .map((filter) => (
            <FilterCard
              key={filter.filterId}
              name={filter.description!}
              content={filter.content}
              onDelete={() => deactivateFilter(filter.filterId)}
            />
          ))}
      </div>
      {showCounter && (
        <TextFractionSpan count={filteredCount} allCount={allCount} className={styles.indicator} />
      )}
    </div>
  );
};
